import SettingsShell from "@/modules/settings/SettingsShell";
import CustomDrawer from "@/components/drawer";
import {
  MerchantWorkScheduleCommand,
  MerchantWorkScheduleWeeKDayCommand,
} from "@/models/club";
import { formatTime, WeekDays } from "@/modules/team/addShift/type";
import LoadingWorkingHours from "@/modules/workingHours/Loading";
import {
  useGetWorkingHours,
  useUpdateWorkingHours,
} from "@/services/workingHours/utils";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCircleFilled, IconPencil } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useEffect, useState } from "react";
import AddTimeSlot from "./AddTimeSlot";
import {
  MerchantWorkSchedulePayload,
  schemaMerchantWorkScheduleCommand,
} from "./type";
import useIsSmall from "@/hooks/useIsSmall";

function WorkingHours() {
  const [opened, { open, close }] = useDisclosure(false);

  const [openedEdit, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const isSmall = useIsSmall();
  const [selectedWeekDay, setSelectedWeekDay] = useState<number | undefined>(
    undefined
  );

  const { data, isLoading } = useGetWorkingHours();

  const response = data as MerchantWorkScheduleCommand;
  const { mutate: updateWorkingHours, isLoading: isUpdatingWorkingHours } =
    useUpdateWorkingHours();

  const form = useForm<MerchantWorkSchedulePayload>({
    initialValues: {
      weekDays: [],
      autoGenerateDaysCount: 0,
      needAutoGenerateDays: false,
    },
    validate: yupResolver(schemaMerchantWorkScheduleCommand),
  });

  const submit = (values: MerchantWorkSchedulePayload) => {
    updateWorkingHours(
      {
        weekDays: values?.weekDays?.map((x) => ({
          weekDay: x.weekDay,
          timeSlots: x.timeSlots?.map((y) => ({
            startTime: `${String(y.startTime?.hour ?? 0).padStart(
              2,
              "0"
            )}:${String(y.startTime?.minute ?? 0).padStart(2, "0")}:${String(
              y.startTime?.second ?? 0
            ).padStart(2, "0")}`,
            endTime: `${String(y.endTime?.hour ?? 0).padStart(2, "0")}:${String(
              y.endTime?.minute ?? 0
            ).padStart(2, "0")}:${String(y.endTime?.second ?? 0).padStart(
              2,
              "0"
            )}`,
          })),
        })) as MerchantWorkScheduleWeeKDayCommand[],
        autoGenerateDaysCount: values?.needAutoGenerateDays
          ? values.autoGenerateDaysCount
          : 0,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: "عملیات موفق",
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          close();
        },
      }
    );
  };

  useEffect(() => {
    if (opened) {
      form.setValues({
        weekDays: response?.weekDays?.map((x) => ({
          weekDay: x.weekDay,
          timeSlots: x.timeSlots?.map((y) => ({
            startTime: {
              hour: moment(y.startTime, "HH:mm:ss").hour(),
              minute: moment(y.startTime, "HH:mm:ss").minute(),
            },
            endTime: {
              hour: moment(y.endTime, "HH:mm:ss").hour(),
              minute: moment(y.endTime, "HH:mm:ss").minute(),
            },
          })),
        })) as MerchantWorkScheduleWeeKDayCommand[],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        autoGenerateDaysCount: response?.autoGenerateDaysCount?.toString(),
        needAutoGenerateDays:
          (response?.autoGenerateDaysCount || 0) > 0 ? true : false,
      });
    }
  }, [opened]);

  return (
    <>
      <SettingsShell title="ساعت کاری" description="روزها و ساعت‌هایی که سالن باز است و نوبت آنلاین گرفته می‌شود.">
        <Container size="md" w="100%" px={0}>
          <Stack
            px="lg"
            styles={{
              root: {
                flexGrow: 1,
              },
            }}
          >
            <Group justify="space-between">
              <span />
              <Button
                variant="default"
                radius="xl"
                size="xs"
                onClick={open}
                disabled={isLoading}
              >
                ویرایش
              </Button>
            </Group>
            <Stack gap="lg">
              <Text size="sm" c="dimmed" ta="justify">
                ساعت کاری شما در پروفایل نمایش داده می‌شود و به‌صورت خودکار برای
                تیم شما استفاده می‌شود.
              </Text>
            </Stack>
            <Stack>
              {isLoading ? (
                <>
                  <Stack mt="lg" gap="md">
                    <LoadingWorkingHours />
                  </Stack>
                </>
              ) : (
                <Stack gap="md" mt="lg">
                  {[1, 2, 3, 4, 5, 6, 7].map((item, index) => (
                    <>
                      <Group justify="space-between">
                        <Group gap="5">
                          <IconCircleFilled
                            size="15"
                            color={
                              response?.weekDays?.find((d) => d.weekDay === item)
                                ?.timeSlots?.length
                                ? "var(--mantine-color-green-5)"
                                : "var(--mantine-color-gray-5)"
                            }
                          />
                          <Text size="sm" fw="500">
                            {WeekDays[item as keyof typeof WeekDays]}
                          </Text>
                        </Group>
                        <Group gap="5">
                          <Stack gap="0">
                            {response?.weekDays?.find((d) => d.weekDay === item)
                              ?.timeSlots?.length ? (
                              response?.weekDays
                                ?.find((d) => d.weekDay === item)
                                ?.timeSlots?.map((timeSlot) => (
                                  <Text size="sm">
                                    {moment(timeSlot.endTime, "HH:mm:ss").format(
                                      "HH:mm"
                                    )}{" "}
                                    <Text span px="5" c="dimmed">
                                      تا
                                    </Text>{" "}
                                    {moment(
                                      timeSlot.startTime,
                                      "HH:mm:ss"
                                    ).format("HH:mm")}
                                  </Text>
                                ))
                            ) : (
                              <Text size="xs" c="gray.5">
                                کار نمیکنم
                              </Text>
                            )}
                          </Stack>
                        </Group>
                      </Group>
                      {index !== 6 && <Divider />}
                    </>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Container>
      </SettingsShell>

      <Modal
        opened={!isSmall && opened}
        onClose={() => {
          form.reset();
          close();
        }}
        title="ئغییر ساعت کاری مجموعه"
        size="xl"
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: "91svh",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack px="md">
              <form
                onSubmit={form.onSubmit(submit)}
                name="add-schedule"
                className="w-100"
                id="edit-work-hours"
              >
                <Stack>
                  <Stack>
                    <Stack mt="lg">
                      <Checkbox
                        size="md"
                        label="ایجاد خودکار شیفت کاری تیم"
                        checked={form?.values?.needAutoGenerateDays}
                        {...form.getInputProps("needAutoGenerateDays")}
                      />
                      <Text size="xs" c="dimmed" fw="300" ta="justify">
                        با انتخاب این گزینه ، پس از افزودن هر عضو به تیم، به صورت
                        خودکار شیف کاری پیش فرض با توجه به ساعت کاری مجموعه
                        ایجاد میشود. شما همچنین می توانید به صورت فردی شیفت کاری
                        تیم را در منو مربوطه تغییر دهید.
                      </Text>
                      {form?.values?.needAutoGenerateDays && (
                        <Select
                          size="lg"
                          styles={{
                            label: {
                              fontSize: "1rem",
                            },
                            error: {
                              fontSize: "0.8rem",
                            },
                          }}
                          label="انتخاب زمان پیش فرض ایجاد شیفت کاری"
                          {...form.getInputProps("autoGenerateDaysCount")}
                          data={[
                            {
                              label: "1 هفته",
                              value: "7",
                            },
                            {
                              label: "2 هفته",
                              value: "14",
                            },
                            {
                              label: "3 هفته",
                              value: "21",
                            },

                            {
                              label: "1 ماه",
                              value: "60",
                            },
                            {
                              label: "2 ماه",
                              value: "90",
                            },
                            {
                              label: "3 ماه",
                              value: "120",
                            },
                            {
                              label: "6 ماه",
                              value: "180",
                            },
                            {
                              label: "1 سال",
                              value: "360",
                            },
                          ]}
                        ></Select>
                      )}
                    </Stack>
                    <Stack gap="0" mt="lg">
                      <Title order={5} c={form.errors.weekDays ? "red" : ""}>
                        روزهای هفته
                      </Title>
                    </Stack>
                    <Stack gap="md">
                      {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                        <>
                          <Group justify="space-between">
                            <Group gap="5">
                              <IconCircleFilled
                                size="15"
                                color={
                                  form.values?.weekDays?.find(
                                    (d) => d.weekDay === item
                                  )?.timeSlots?.length
                                    ? "var(--mantine-color-green-5)"
                                    : "var(--mantine-color-gray-5)"
                                }
                              />
                              <Text size="sm" fw="500">
                                {WeekDays[item as keyof typeof WeekDays]}
                              </Text>
                            </Group>
                            <Group gap="5">
                              <Stack gap="0" align="flex-end">
                                {form.values?.weekDays?.find(
                                  (d) => d.weekDay === item
                                )?.timeSlots?.length ? (
                                  form.values?.weekDays
                                    ?.find((d) => d.weekDay === item)
                                    ?.timeSlots?.map((timeSlot) => (
                                      <Text size="sm">
                                        {formatTime(
                                          timeSlot.endTime?.hour,
                                          timeSlot.endTime?.minute
                                        )}
                                        <Text span px="5" c="dimmed">
                                          {" "}
                                          -{" "}
                                        </Text>
                                        {formatTime(
                                          timeSlot.startTime?.hour,
                                          timeSlot.startTime?.minute
                                        )}{" "}
                                      </Text>
                                    ))
                                ) : (
                                  <Text size="xs" c="gray.5">
                                    کار نمیکنم
                                  </Text>
                                )}
                              </Stack>
                              <ActionIcon
                                variant="subtle"

                                onClick={() => {
                                  setSelectedWeekDay(item);
                                  openEdit();
                                }}
                              >
                                <IconPencil stroke={1} />
                              </ActionIcon>
                            </Group>
                          </Group>
                          <Divider />
                        </>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </form>
            </Stack>
          </Box>

          <Card
            styles={{
              root: {
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              },
            }}
            radius={0}
          >
            <Button
              type="submit"
              form="edit-work-hours"
              size="md"

              fullWidth
              loading={isUpdatingWorkingHours}
            >
              ثبت تغییرات
            </Button>
          </Card>
        </Box>
      </Modal>

      <CustomDrawer
        opened={opened && isSmall}
        onClose={() => {
          form.reset();
          close();
        }}
        close={close}
        position="bottom"
        radius="md"
        size="xl"
        title="ئغییر ساعت کاری مجموعه"
        custom="edit-merchant-address"
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: "91svh",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack px="md">
              <form
                onSubmit={form.onSubmit(submit)}
                name="add-schedule"
                className="w-100"
                id="edit-work-hours"
              >
                <Stack>
                  <Stack>
                    <Stack mt="lg">
                      <Checkbox
                        size="md"
                        label="ایجاد خودکار شیفت کاری تیم"
                        checked={form?.values?.needAutoGenerateDays}
                        {...form.getInputProps("needAutoGenerateDays")}
                      />
                      <Text size="xs" c="dimmed" fw="300" ta="justify">
                        با انتخاب این گزینه ، پس از افزودن هر عضو به تیم، به صورت
                        خودکار شیف کاری پیش فرض با توجه به ساعت کاری مجموعه
                        ایجاد میشود. شما همچنین می توانید به صورت فردی شیفت کاری
                        تیم را در منو مربوطه تغییر دهید.
                      </Text>
                      {form?.values?.needAutoGenerateDays && (
                        <Select
                          size="lg"
                          styles={{
                            label: {
                              fontSize: "1rem",
                            },
                            error: {
                              fontSize: "0.8rem",
                            },
                          }}
                          label="انتخاب زمان پیش فرض ایجاد شیفت کاری"
                          {...form.getInputProps("autoGenerateDaysCount")}
                          data={[
                            {
                              label: "1 هفته",
                              value: "7",
                            },
                            {
                              label: "2 هفته",
                              value: "14",
                            },
                            {
                              label: "3 هفته",
                              value: "21",
                            },

                            {
                              label: "1 ماه",
                              value: "60",
                            },
                            {
                              label: "2 ماه",
                              value: "90",
                            },
                            {
                              label: "3 ماه",
                              value: "120",
                            },
                            {
                              label: "6 ماه",
                              value: "180",
                            },
                            {
                              label: "1 سال",
                              value: "360",
                            },
                          ]}
                        ></Select>
                      )}
                    </Stack>
                    <Stack gap="0" mt="lg">
                      <Title order={5} c={form.errors.weekDays ? "red" : ""}>
                        روزهای هفته
                      </Title>
                    </Stack>
                    <Stack gap="md">
                      {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                        <>
                          <Group justify="space-between">
                            <Group gap="5">
                              <IconCircleFilled
                                size="15"
                                color={
                                  form.values?.weekDays?.find(
                                    (d) => d.weekDay === item
                                  )?.timeSlots?.length
                                    ? "var(--mantine-color-green-5)"
                                    : "var(--mantine-color-gray-5)"
                                }
                              />
                              <Text size="sm" fw="500">
                                {WeekDays[item as keyof typeof WeekDays]}
                              </Text>
                            </Group>
                            <Group gap="5">
                              <Stack gap="0" align="flex-end">
                                {form.values?.weekDays?.find(
                                  (d) => d.weekDay === item
                                )?.timeSlots?.length ? (
                                  form.values?.weekDays
                                    ?.find((d) => d.weekDay === item)
                                    ?.timeSlots?.map((timeSlot) => (
                                      <Text size="sm">
                                        {formatTime(
                                          timeSlot.endTime?.hour,
                                          timeSlot.endTime?.minute
                                        )}
                                        <Text span px="5" c="dimmed">
                                          {" "}
                                          -{" "}
                                        </Text>
                                        {formatTime(
                                          timeSlot.startTime?.hour,
                                          timeSlot.startTime?.minute
                                        )}{" "}
                                      </Text>
                                    ))
                                ) : (
                                  <Text size="xs" c="gray.5">
                                    کار نمیکنم
                                  </Text>
                                )}
                              </Stack>
                              <ActionIcon
                                variant="subtle"

                                onClick={() => {
                                  setSelectedWeekDay(item);
                                  openEdit();
                                }}
                              >
                                <IconPencil stroke={1} />
                              </ActionIcon>
                            </Group>
                          </Group>
                          <Divider />
                        </>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </form>
            </Stack>
          </Box>

          <Card
            styles={{
              root: {
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              },
            }}
            withBorder
            radius={0}
          >
            <Button
              type="submit"
              form="edit-work-hours"
              size="md"

              fullWidth
              loading={isUpdatingWorkingHours}
            >
              ثبت تغییرات
            </Button>
          </Card>
        </Box>
      </CustomDrawer>
      {openedEdit && (
        <AddTimeSlot
          opened={openedEdit}
          close={closeEdit}
          selectedWeekDay={selectedWeekDay as number}
          existingTimeSlots={
            form.values.weekDays?.find((d) => d.weekDay === selectedWeekDay)
              ?.timeSlots
          }
          addTimeSlotToDay={(timeSlots) => {
            const weekDays = form.values.weekDays ?? [];
            const existingWeekDayIndex = weekDays.findIndex(
              (d) => d.weekDay === timeSlots.weekDay
            );
            if (existingWeekDayIndex !== -1) {
              // Update existing weekday's timeslots
              const updatedWeekDays = [...weekDays];
              updatedWeekDays[existingWeekDayIndex] = timeSlots;
              form.setFieldValue("weekDays", updatedWeekDays);
            } else {
              // Add new weekday
              form.setFieldValue("weekDays", [...weekDays, timeSlots]);
            }
          }}
        />
      )}
    </>
  );
}

export default WorkingHours;
